import LegalLayout from "@/pages/legal/LegalLayout";
import { LEGAL } from "@/lib/legal";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

const S = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-20">
    <h2 className="font-heading text-2xl font-bold tracking-tight text-white mt-10 mb-3">
      {title}
    </h2>
    <div className="space-y-4 text-zinc-300 text-[15px] leading-relaxed">{children}</div>
  </section>
);

/**
 * Data Processing Agreement (DPA)
 * -------------------------------
 * GDPR-compliant DPA. Aimed at business customers whose photos may include
 * identifiable persons (e.g. real-estate agencies, marketing agencies) and
 * who therefore need a Controller-Processor agreement in place before they
 * can lawfully upload content to MediaOthers.
 *
 * Users can print/save this page as PDF from their browser (Ctrl/Cmd + P).
 */
export default function DPA() {
  const printPdf = () => {
    if (typeof window !== "undefined") window.print();
  };

  return (
    <LegalLayout
      testid="dpa"
      title="Data Processing Agreement"
      subtitle={`For business customers who need a Controller–Processor agreement with ${LEGAL.company} under Article 28 GDPR.`}
    >
      <div className="not-prose flex flex-wrap items-center gap-3 my-2">
        <Button
          onClick={printPdf}
          data-testid="dpa-download"
          className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
        >
          <Download className="w-4 h-4 mr-2" strokeWidth={2} />
          Save as PDF
        </Button>
        <span className="text-[11px] font-mono-tech text-zinc-500 uppercase tracking-wider">
          Use your browser&apos;s Print → Save as PDF (Ctrl/Cmd + P)
        </span>
      </div>

      <S id="parties" title="1. Parties">
        <p>This Data Processing Agreement (&quot;DPA&quot;) is entered into between:</p>
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-amber-400/60">
          <li>
            <strong>Processor:</strong> {LEGAL.company} (trading as {LEGAL.brand}), a company
            registered in {LEGAL.country} — KvK {LEGAL.kvk || "—"}
            {LEGAL.establishment ? `, establishment ${LEGAL.establishment}` : ""}.
          </li>
          <li>
            <strong>Controller:</strong> the {LEGAL.brand} customer whose account details
            (business name, contact person, email) are recorded in their MediaOthers
            workspace at the time of acceptance of this DPA.
          </li>
        </ul>
        <p>
          Acceptance takes place by any of the following actions by an authorised
          representative of the Controller: (i) creating a paid subscription, (ii)
          purchasing credits, or (iii) uploading content that qualifies as personal data
          under the GDPR to {LEGAL.brand}.
        </p>
      </S>

      <S id="scope" title="2. Subject-matter, duration, nature &amp; purpose">
        <p>
          The Processor processes personal data on behalf of the Controller solely to
          provide the {LEGAL.brand} platform: transforming photos and prompts into videos
          using AI, storing generated deliverables, sending transactional emails and
          providing customer support.
        </p>
        <p>
          This DPA is in effect for as long as the Controller uses {LEGAL.brand} and any
          period thereafter during which the Processor still holds personal data on their
          behalf.
        </p>
      </S>

      <S id="data" title="3. Types of personal data &amp; categories of data subjects">
        <p><strong>Types of personal data</strong> that may be processed:</p>
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-amber-400/60">
          <li>Contact details of the Controller&apos;s account users (name, business email).</li>
          <li>Photographs uploaded by the Controller that may depict identifiable individuals (residents, tenants, employees, agents) or private property.</li>
          <li>Prompt text and creative direction provided by the Controller.</li>
          <li>Usage metadata (timestamps, credits consumed, error events).</li>
        </ul>
        <p><strong>Categories of data subjects</strong>: employees / agents of the Controller and, incidentally, third parties whose likeness or property is depicted in uploaded photos.</p>
      </S>

      <S id="obligations" title="4. Processor obligations">
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-amber-400/60">
          <li>Process personal data only on documented instructions of the Controller, including for transfers to third countries where relevant.</li>
          <li>Ensure that all personnel authorised to process personal data are bound by confidentiality.</li>
          <li>Implement appropriate technical and organisational measures (Section 6).</li>
          <li>Assist the Controller in fulfilling data-subject rights requests (access, rectification, erasure, portability, objection).</li>
          <li>Assist the Controller in meeting its obligations under Articles 32–36 GDPR, including breach notification and Data Protection Impact Assessments.</li>
          <li>Make available all information necessary to demonstrate compliance and allow audits (Section 8).</li>
          <li>Delete or return personal data at the end of the service, at the Controller&apos;s choice, unless retention is required by Union or Member-State law.</li>
        </ul>
      </S>

      <S id="subprocessors" title="5. Sub-processors">
        <p>
          The Controller grants a <strong>general written authorisation</strong> for the
          Processor to engage sub-processors as necessary to deliver the service. The
          Processor maintains an up-to-date list of sub-processors in the
          {" "}<a href="/legal/privacy" className="text-amber-400 hover:underline">Privacy Policy</a>.
          The Processor will impose data-protection obligations on any sub-processor equivalent to those in this DPA and remains liable to the Controller for the acts and omissions of its sub-processors.
        </p>
        <p>
          If the Processor engages a new sub-processor, the Controller will be notified by
          email at least 14 days in advance and may object on reasonable data-protection
          grounds.
        </p>
      </S>

      <S id="security" title="6. Security measures (Art. 32 GDPR)">
        <p>The Processor has implemented the following measures, at minimum:</p>
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-amber-400/60">
          <li>All customer traffic served over HTTPS with modern TLS.</li>
          <li>Passwords stored as bcrypt hashes; multi-factor authentication supported for admin access.</li>
          <li>Access to production systems restricted to a small number of named operators and logged.</li>
          <li>Automatic pruning of failed-render technical logs after 90 days.</li>
          <li>Auto-mute of unstable AI-engine providers to reduce processing exposure during incidents.</li>
          <li>Regular application updates and security patches.</li>
        </ul>
      </S>

      <S id="transfers" title="7. International transfers">
        <p>
          Personal data may be transferred outside the European Economic Area only when
          appropriate safeguards under Chapter V GDPR are in place — typically the
          European Commission&apos;s Standard Contractual Clauses (SCCs) or an adequacy
          decision. Any AI-engine sub-processor involving transfers outside the EEA is
          contractually bound to equivalent safeguards.
        </p>
      </S>

      <S id="audit" title="8. Audit rights">
        <p>
          Once per calendar year, and with at least 30 days&apos; written notice, the
          Controller may request an audit of the Processor&apos;s compliance with this DPA.
          Audits will be conducted during business hours in a manner that does not
          unreasonably disrupt the Processor&apos;s operations. Where a recent independent
          audit report or industry certification is available, the Processor may share
          this in lieu of an on-premise audit.
        </p>
      </S>

      <S id="breach" title="9. Data breach notification">
        <p>
          The Processor will notify the Controller without undue delay, and in any event
          within 48 hours, after becoming aware of a personal-data breach affecting the
          Controller&apos;s data. The notification will include the nature of the breach,
          the categories and approximate number of data subjects affected, likely
          consequences and the measures taken or proposed.
        </p>
      </S>

      <S id="termination" title="10. Termination &amp; return of data">
        <p>
          On termination of the underlying service, the Controller may export their data
          via self-service tooling or by written request to{" "}
          <a className="text-amber-400 hover:underline" href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a>.
          After a grace period of 30 days, and unless the Controller elects earlier
          deletion, all remaining personal data will be permanently erased from
          production systems and from routine backups within 90 days.
        </p>
      </S>

      <S id="law" title="11. Governing law &amp; jurisdiction">
        <p>
          This DPA is governed by the laws of {LEGAL.country}. Disputes will be brought
          before the competent Dutch courts.
        </p>
      </S>

      <S id="acceptance" title="12. Acceptance">
        <p>
          By using {LEGAL.brand} under a paid subscription or credit purchase, the
          Controller confirms that an authorised representative has read, understood and
          agrees to be bound by this DPA. If the Controller requires a countersigned
          paper copy, please write to{" "}
          <a className="text-amber-400 hover:underline" href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a>
          {" "}with the business name, contact person, KvK number (if applicable) and
          preferred signing method — we will provide a countersigned PDF within 5 business
          days.
        </p>
      </S>
    </LegalLayout>
  );
}
